import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LeaningVideo from '@/components/organisms/Learning/Video';
import LeaningFlashCard from '@/components/organisms/Learning/FlashCard';
import SpellCheck from '@/components/organisms/Learning/SpellCheck';
import AlphabetChoice from '@/components/organisms/Learning/AlphabetChoice';
import DragDrap from '@/components/organisms/Learning/DragMatch';
import WordChoice from '@/components/organisms/Learning/WordChoice';
import PartWriting from '@/components/organisms/Learning/PartWriting';
import Diagram from '@/components/organisms/Learning/Diagram';
import Dictation from '@/components/organisms/Learning/Dictation';
import FillBlank from '@/components/organisms/Learning/FillBlank';
import EngWritng from '@/components/organisms/Learning/EngWritng';
import FreeWritng from '@/components/organisms/Learning/FreeWritng';
import StoryDialog from '@/components/organisms/Learning/StoryDialog';
import StoryDialogQustion from '@/components/organisms/Learning/StoryDialogQustion';
import Complete from '@/components/organisms/Learning/Complete';
import { Button } from '@/components/atoms/Button/Button';
import { Modal } from '@/components/organisms/Modal/Dialog';
import { studentUnitApi } from '@/services/api';

interface Activity {
    id: number;
    activityType: string;
    title: string;
    instructions: string;
    configJson: string;
    orderIndex: number;
    items: any[];
}

interface UnitData {
    unitId: number;
    unitCode: string;
    unitTitle: string;
    unitType: string;
    unitCategory: string;
    textbookTitle: string;
    textbookId?: number;
    scheduleId: number;
    nextUnitId?: number;
    nextUnitTitle?: string;
    dailyLimit?: {
        unlimited: boolean;
        remainingToday: number;
    };
    scheduleTitle: string;
    activities: Activity[];
    totalActivities: number;
    videoUrl?: string;
    videoQuiz?: string;
    configJson?: string;
}

// 활동 타입별 컴포넌트 매핑
const ActivityComponents: Record<string, React.ComponentType<any>> = {
    'VIDEO_QUIZ': LeaningVideo, // 동영상 퀴즈
    'DIAGRAM': Diagram,
    'DICTATION': Dictation,
    'FILL_BLANK': FillBlank,
    'SPELL_CHECK': SpellCheck,
    'ALPHABET_CHOICE': AlphabetChoice,
    'FLASH_CARD': LeaningFlashCard,
    'STORY_DIALOG': StoryDialog,
    'STORY_DIALOG_QA': StoryDialogQustion,
    'WORD_CHOICE': WordChoice,
    'DRAG_MATCH': DragDrap,
    'FREE_WRITING': FreeWritng,
    'ENG_WRITING': EngWritng,
    'PART_WRITING': PartWriting,
};

// Unit Type별 학습 활동 구성 정의 (DB activity_type 기반)
// DB의 activity를 어떻게 확장/변환할지 정의
const UNIT_TYPE_CONFIG: Record<string, {
    // DB에서 가져올 activity_type들
    allowedDbActivities: string[];
    // FLASH_CARD를 확장할 추가 문제 유형들 (순서대로 생성됨)
    flashCardExtensions?: string[];
    // WORD_CHOICE를 확장할 추가 문제 유형들
    wordChoiceExtensions?: string[];
    // 드래그매치 최대 개수
    dragMatchMaxItems?: number | null;
}> = {
    'PHONICS_DAY': {
        // 동영상퀴즈(등록시), 플래시카드, 드래그매치, 스펠체크, 알파벳고르기
        allowedDbActivities: ['DICTATION', 'FLASH_CARD'], // DICTATION = 동영상 퀴즈 (videoQuizEnabled로 구분)
        flashCardExtensions: ['DRAG_MATCH', 'SPELL_CHECK', 'ALPHABET_CHOICE'],
        dragMatchMaxItems: null,
    },
    'STARTER_PHONICS': {
        // 동영상퀴즈(등록시), 플래시카드, 드래그매치, 스펠체크, 알파벳고르기
        allowedDbActivities: ['DICTATION', 'FLASH_CARD'], // DICTATION = 동영상 퀴즈 (videoQuizEnabled로 구분)
        flashCardExtensions: ['DRAG_MATCH', 'SPELL_CHECK'],
        dragMatchMaxItems: null, // null이면 모든 아이템을 하나로 묶음, SPELL_CHECK/ALPHABET_CHOICE는 각각 개별 생성
    },
    'STARTER_NUANCE': {
        // 동영상퀴즈(등록시), 플래시카드, 드래그매치(최대6개), 단어고르기&녹음, 부분영작
        // 부분영작은 단어고르기 데이터를 사용함
        // 주의: 동영상 퀴즈는 DB에서 DICTATION으로 저장되지만 videoQuizEnabled=true로 구분됨
        allowedDbActivities: ['FLASH_CARD', 'WORD_CHOICE', 'DICTATION'],
        flashCardExtensions: ['DRAG_MATCH'],
        wordChoiceExtensions: ['PART_WRITING'], // WORD_CHOICE 데이터로 부분영작 생성
        dragMatchMaxItems: 6,
    },
    'STARTER_STRUCTURE': {
        // 동영상퀴즈(등록시), 플래시카드, 드래그매치(최대6개), 다이어그램, 부분영작&녹음
        // 부분영작은 단어고르기 데이터를 사용함 (WORD_CHOICE는 화면에 표시 안 함)
        allowedDbActivities: ['DICTATION', 'FLASH_CARD', 'DIAGRAM'],
        flashCardExtensions: ['DRAG_MATCH'],
        wordChoiceExtensions: ['PART_WRITING'], // WORD_CHOICE 데이터로 부분영작 생성 (확장용)
        dragMatchMaxItems: 6,
    },
    'ESSAY': {
        // 동영상, 딕테이션(WORD_CHOICE→DICTATION 변환), 드래그매치(FLASH_CARD→DRAG_MATCH 변환), 빈칸채우기, 영작, 프리라이팅
        // DB 활동을 모두 가져오고, 확장 처리 시 변환
        // PART_WRITING = 빈칸채우기, ENG_WRITING = 영작
        allowedDbActivities: ['DICTATION', 'WORD_CHOICE', 'FLASH_CARD', 'PART_WRITING', 'ENG_WRITING', 'FREE_WRITING'],
        // WORD_CHOICE는 DICTATION으로 변환, FLASH_CARD는 DRAG_MATCH로 변환
        dragMatchMaxItems: 6,
    },
    'TRANSLATION': {
        // 동영상퀴즈(등록시), 딕테이션(WORD_CHOICE→DICTATION), 드래그매치(FLASH_CARD→DRAG_MATCH), 빈칸채우기, 영작, 프리라이팅
        allowedDbActivities: ['DICTATION', 'WORD_CHOICE', 'FLASH_CARD', 'PART_WRITING', 'FILL_BLANK', 'ENG_WRITING', 'FREE_WRITING'],
        dragMatchMaxItems: 6,
    },
    'READING_BOOK': {
        // 플래시카드, 드래그매치, 스토리&다이얼로그, 스토리&다이얼로그_퀘스천
        allowedDbActivities: ['FLASH_CARD', 'STORY_DIALOG', 'STORY_DIALOG_QA'],
        flashCardExtensions: ['DRAG_MATCH'],
        dragMatchMaxItems: null,
    },
};

interface FlatItem {
    activity: Activity;
    item: any;
    globalIndex: number; // 전체 아이템 중 인덱스
    activityIndex: number; // 해당 activity의 인덱스
    itemIndex: number; // activity 내에서의 item 인덱스
}

const getResumeStep = (savedResponses?: Record<string, any> | null) => {
    if (!savedResponses) return 0;
    const indices = Object.keys(savedResponses)
        .map((key) => Number(key))
        .filter((num) => Number.isFinite(num))
        .sort((a, b) => a - b);
    if (indices.length === 0) {
        return 0;
    }

    for (const idx of indices) {
        const resp = savedResponses[idx];
        if (!resp || resp.isCorrect !== true) {
            return idx;
        }
    }

    return indices[indices.length - 1] + 1;
};

export default function StudentLeaning() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const scheduleId = searchParams.get('scheduleId');
    const unitId = searchParams.get('unitId');
    
    // 현재 경로 확인 (학습 or 숙제)
    const isHomework = window.location.pathname.includes('/homework/');
    
    const [step, setStep] = useState(0);  // 현재 아이템 인덱스 (전체)
    const [unitData, setUnitData] = useState<UnitData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [wasCompleted, setWasCompleted] = useState(false);
    
    // 각 아이템별 응답 데이터 저장
    const [responses, setResponses] = useState<Record<number, any>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [submitValidator, setSubmitValidator] = useState<(() => Promise<boolean> | boolean) | null>(null);
    const isReadOnlyMode = wasCompleted;

    // unitId/scheduleId 변경 시 로컬 상태 초기화
    useEffect(() => {
        setStep(0);
        setResponses({});
        setWasCompleted(false);
    }, [unitId, scheduleId]);

    // 유닛 학습 데이터 조회
    const fetchUnitData = async () => {
        if (!unitId || !scheduleId) {
            const errorMsg = `필수 파라미터가 누락되었습니다. (unitId: ${unitId}, scheduleId: ${scheduleId})`;
            console.error(errorMsg);
            setError(errorMsg);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            console.log('=== 유닛 학습 데이터 조회 시작 ===');
            console.log('Unit ID:', unitId);
            console.log('Schedule ID:', scheduleId);
            console.log('API 호출 URL:', `/student/unit/${unitId}/learning-data?scheduleId=${scheduleId}`);
            
            const response = await studentUnitApi.getLearningData(Number(unitId), Number(scheduleId));
            
            console.log('=== API 응답 수신 ===');
            console.log('Response:', response);
            console.log('Response.success:', response?.success);
            console.log('Response.data:', response?.data);
            
            if (response?.success && response?.data) {
                console.log('✅ 데이터 로드 성공');
                setUnitData(response.data);
                console.log('유닛 타입:', response.data.unitType);
                console.log('활동 개수:', response.data.activities?.length || 0);

                // 이미 완료된 숙제/학습인지 기록 (재진입 시 상태 강등 방지)
                try {
                    const statusVal = (response.data.status || '').toString().toUpperCase();
                    const progressVal = Number(response.data.progress || response.data.progressPercentage || 0);
                    const completedFlag = Boolean(response.data.completed);
                    const isCompleted = (statusVal === 'COMPLETED') || completedFlag || progressVal === 100;
                    setWasCompleted(isCompleted);
                } catch {}
                
                // 기존 진행 상황 복원
                if (response.data.responseJson) {
                    try {
                        const savedResponses = typeof response.data.responseJson === 'string'
                            ? JSON.parse(response.data.responseJson)
                            : response.data.responseJson;
                        
                        console.log('=== 저장된 응답 복원 ===');
                        console.log('Saved Responses:', savedResponses);
                        
                        setResponses(savedResponses);
                        
                        const resumeStep = getResumeStep(savedResponses);
                        console.log(`복원된 학습 위치: ${resumeStep}`);
                        setStep(resumeStep);
                    } catch (e) {
                        console.error('responseJson 파싱 에러:', e);
                        setResponses({});
                        setStep(0);
                    }
                } else {
                    setResponses({});
                    setStep(0);
                }
            } else {
                const errorMsg = response?.message || '유닛 데이터를 불러오는데 실패했습니다.';
                console.error('❌ API 실패:', errorMsg);
                setError(errorMsg);
            }
        } catch (err: any) {
            console.error('❌ 유닛 데이터 조회 중 예외 발생:', err);
            console.error('Error details:', {
                message: err?.message,
                response: err?.response,
                status: err?.response?.status,
                data: err?.response?.data
            });
            
            let errorMsg = '유닛 데이터를 불러오는 중 오류가 발생했습니다.';
            
            if (err?.response?.status === 401) {
                errorMsg = '로그인이 필요합니다. 다시 로그인해주세요.';
            } else if (err?.response?.status === 403) {
                errorMsg = '접근 권한이 없습니다.';
            } else if (err?.response?.status === 404) {
                errorMsg = '요청한 학습 데이터를 찾을 수 없습니다.';
            } else if (err?.response?.data?.message) {
                errorMsg = err.response.data.message;
            }
            
            setError(errorMsg);
        } finally {
            console.log('=== 데이터 조회 완료 (로딩 종료) ===');
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUnitData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitId, scheduleId]);

    const isPlainObject = (value: any) => {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
    };

    useEffect(() => {
        setSubmitValidator(null);
    }, [step]);

    // 현재 활동 응답 처리
    const handleResponse = (activityIndex: number, response: any) => {
        if (isReadOnlyMode) {
            return;
        }

        setResponses(prev => {
            const prevResponse = prev[activityIndex] || {};
            let nextResponse = response;

            if (isPlainObject(response)) {
                const { __replaceResponse, ...rest } = response as Record<string, any>;
                if (__replaceResponse) {
                    nextResponse = rest;
                } else {
                    nextResponse = { ...prevResponse, ...rest };
                }
            } else if (response === undefined) {
                nextResponse = prevResponse;
            }

            const updated = {
                ...prev,
                [activityIndex]: nextResponse
            };

            console.log(`=== 활동 ${activityIndex} 응답 저장 ===`);
            console.log('활동 타입:', flatItems[activityIndex]?.activity.activityType);
            console.log('이전 응답:', prevResponse);
            console.log('새 응답:', response);
            console.log('머지 결과:', updated[activityIndex]);

            return updated;
        });
    };

    if (isLoading) {
        return (
            <main className='flex flex-1 w-full mt-10 bg-transparent shadow-md !rounded-t-2xl md:my-10 md:justify-center card'>
                <div className='flex justify-center items-center w-full h-64'>
                    <p className='text-gray-500'>유닛 데이터를 불러오는 중...</p>
                </div>
            </main>
        );
    }

    if (error || !unitData) {
        return (
            <main className='flex flex-1 w-full mt-10 bg-transparent shadow-md !rounded-t-2xl md:my-10 md:justify-center card'>
                <div className='flex flex-col justify-center items-center w-full h-64'>
                    <p className='mb-4 text-red-500'>{error || '유닛 데이터를 찾을 수 없습니다.'}</p>
                    <Button onClick={() => navigate(-1)}>
                        돌아가기
                    </Button>
                </div>
            </main>
        );
    }

    const goToDetailPage = () => {
        const detailBasePath = isHomework ? `/homework/detail/${scheduleId}` : `/learning/detail/${scheduleId}`;
        let detailPath = detailBasePath;
        if (!isHomework) {
            const detailParams = new URLSearchParams();
            if (unitData?.textbookId) {
                detailParams.set('textbookId', String(unitData.textbookId));
            } else if (unitData?.textbookTitle) {
                detailParams.set('textbookTitle', unitData.textbookTitle);
            }
            const qs = detailParams.toString();
            if (qs) {
                detailPath = `${detailBasePath}?${qs}`;
            }
        }
        navigate(detailPath);
    };

    const activities = unitData.activities || [];
    const unitType = unitData.unitType;
    const config = UNIT_TYPE_CONFIG[unitType];
    
    console.log('=== 유닛 데이터 확인 ===');
    console.log('Unit Type:', unitType);
    console.log('Unit Title:', unitData.unitTitle);
    console.log('Unit Data (전체):', unitData);
    console.log('DB에서 가져온 전체 활동 목록:');
    activities.forEach((act, idx) => {
        console.log(`  ${idx + 1}. ${act.activityType} - ${act.title} (아이템 수: ${act.items?.length || 0})`);
    });
    
    if (!config) {
        console.error('지원하지 않는 Unit Type:', unitType);
        return null;
    }
    
    console.log('=== 활동 구성 ===');
    console.log('허용되는 DB 활동:', config.allowedDbActivities);
    console.log('플래시카드 확장:', config.flashCardExtensions);
    console.log('드래그매치 최대 개수:', config.dragMatchMaxItems);
    
    // 1. DB 활동 필터링
    const filteredActivities = activities.filter(activity => {
        const isAllowed = config.allowedDbActivities.includes(activity.activityType);
        console.log(`활동 ${activity.activityType}: ${isAllowed ? '허용됨' : '필터링됨'}`);
        return isAllowed;
    });
    
    // 2. 활동 확장 및 변환 (FLASH_CARD, WORD_CHOICE, DICTATION 등)
    const expandedActivities: Activity[] = [];
    
    filteredActivities.forEach(activity => {
        // ESSAY/TRANSLATION 타입: FLASH_CARD를 DICTATION으로 변환 (각 플래시카드마다 개별 생성)
        if ((unitType === 'ESSAY' || unitType === 'TRANSLATION') && activity.activityType === 'FLASH_CARD') {
            console.log(`${unitType}: FLASH_CARD를 DICTATION으로 변환 (각 플래시카드마다 개별)`);
            
            // 각 플래시카드 아이템마다 개별 DICTATION 활동 생성
            activity.items.forEach((flashCardItem, idx) => {
                expandedActivities.push({
                    ...activity,
                    id: activity.id + Math.random(), // 임시 고유 ID
                    activityType: 'DICTATION',
                    title: '딕테이션',
                    // 현재 플래시카드 아이템만 포함
                    items: [{
                        id: flashCardItem.id,
                        itemType: 'DICTATION',
                        orderIndex: idx,
                        refType: 'FLASH_CARD',
                        refId: flashCardItem.id,
                        contentJson: flashCardItem.contentJson, // 개별 플래시카드 데이터
                        answerJson: null,
                        score: null
                    }]
                });
                console.log(`  → DICTATION 활동 추가됨 (플래시카드 아이템 ${idx + 1}/${activity.items.length})`);
            });
            
            // DRAG_MATCH도 추가 (모든 플래시카드를 하나로 묶음)
            expandedActivities.push({
                ...activity,
                id: activity.id + 1000, // 임시 고유 ID
                activityType: 'DRAG_MATCH',
                title: '드래그 매치',
                items: [{
                    id: activity.id,
                    itemType: 'DRAG_MATCH',
                    orderIndex: 0,
                    refType: 'FLASH_CARD',
                    refId: activity.id,
                    contentJson: JSON.stringify({ flashCards: activity.items }),
                    answerJson: null,
                    score: null
                }]
            });
            console.log(`  → DRAG_MATCH 활동 추가됨 (플래시카드 ${activity.items.length}개를 하나로 묶음)`);
            
            return; // 다음 활동으로
        }
        
        // DICTATION인 경우 videoQuizEnabled/videoUrl 확인
        if (activity.activityType === 'DICTATION') {
            try {
                const activityConfig = JSON.parse(activity.configJson || '{}');
                if (activityConfig.videoQuizEnabled === true || !!activityConfig.videoUrl) {
                    console.log('동영상 퀴즈 발견 (DICTATION with videoQuizEnabled=true)');
                    console.log('videoUrl:', activityConfig.videoUrl);
                    console.log('items 개수:', activity.items?.length || 0);
                    
                    // 동영상 퀴즈로 변환
                    // items가 비어있으면 더미 아이템 생성
                    const videoQuizItems = activity.items && activity.items.length > 0 
                        ? activity.items 
                        : [{
                            id: activity.id,
                            itemType: 'VIDEO_QUIZ',
                            orderIndex: 0,
                            refType: 'NONE',
                            refId: null,
                            contentJson: activity.configJson, // configJson에 videoUrl과 quizzes 정보 포함
                            answerJson: null,
                            score: null
                        }];
                    
                    expandedActivities.push({
                        ...activity,
                        activityType: 'VIDEO_QUIZ',
                        title: activity.title || '동영상 퀴즈',
                        items: videoQuizItems
                    });
                    console.log('동영상 퀴즈 추가됨 (아이템 수:', videoQuizItems.length, ')');
                    return; // 다음 활동으로
                } else {
                    console.log('일반 딕테이션 (videoQuizEnabled=false)');
                }
            } catch (e) {
                console.error('configJson 파싱 에러:', e);
            }
        }
        
        // STORY_DIALOG인 경우 모든 아이템을 하나로 묶음
        if (activity.activityType === 'STORY_DIALOG') {
            console.log(`스토리&다이얼로그: 아이템 ${activity.items.length}개를 하나로 묶음`);
            
            // 모든 아이템을 포함하는 단일 activity 생성
            expandedActivities.push({
                ...activity,
                // items는 그대로 유지 (컴포넌트에서 모든 아이템 처리)
            });
            
            console.log(`  → STORY_DIALOG 활동 추가됨 (아이템 ${activity.items.length}개 포함)`);
            return; // 다음 활동으로
        }
        
        // STORY_DIALOG_QA인 경우도 하나로 묶음 (여러 문제가 있을 수 있음)
        if (activity.activityType === 'STORY_DIALOG_QA') {
            console.log(`스토리&다이얼로그_퀘스천: 아이템 ${activity.items.length}개를 하나로 묶음`);
            
            expandedActivities.push({
                ...activity,
                // items는 그대로 유지
            });
            
            console.log(`  → STORY_DIALOG_QA 활동 추가됨 (아이템 ${activity.items.length}개 포함)`);
            return;
        }
        
        expandedActivities.push(activity);
        
        // FLASH_CARD 확장은 아래 2-2 단계에서 일괄 처리 (여기서는 확장 생성하지 않음)
        if (activity.activityType === 'FLASH_CARD' && config.flashCardExtensions) {
            console.log('플래시카드 확장 생성은 2-2 단계에서 수행합니다. (중복 방지)');
        }
        
        // WORD_CHOICE인 경우 확장 활동 추가 (부분영작 등)
        if (activity.activityType === 'WORD_CHOICE' && config.wordChoiceExtensions) {
            console.log(`단어고르기 확장: ${config.wordChoiceExtensions.join(', ')}`);
            console.log(`단어고르기 아이템 개수: ${activity.items.length}`);
            
            config.wordChoiceExtensions.forEach(extensionType => {
                // PART_WRITING은 문항별로 분리 생성하여 다중 문항을 모두 노출
                if (extensionType === 'PART_WRITING' && activity.items?.length) {
                    activity.items.forEach((wcItem, wcIdx) => {
                        let perItemTitleTranslation = '';
                        try {
                            const wcContent = wcItem?.contentJson ? (typeof wcItem.contentJson === 'string' ? JSON.parse(wcItem.contentJson) : wcItem.contentJson) : {};
                            perItemTitleTranslation = wcContent?.titleTranslation || '';
                        } catch (e) {
                            console.error('wcItem contentJson parse error:', e);
                        }
                        let parentConfigJson = activity.configJson;
                        try {
                            const parsedCfg = activity.configJson ? (typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson) : {};
                            parentConfigJson = JSON.stringify({ ...parsedCfg, titleTranslation: perItemTitleTranslation || parsedCfg.titleTranslation });
                        } catch (e) {
                            console.error('WORD_CHOICE parent config parse error:', e);
                        }
                        const extendedActivity: Activity = {
                            ...activity,
                            id: activity.id + Math.random(),
                            activityType: extensionType,
                            title: '부분 영작',
                            items: [{
                                id: wcItem.id,
                                itemType: extensionType,
                                orderIndex: wcIdx,
                                refType: 'WORD_CHOICE',
                                refId: wcItem.id,
                                contentJson: JSON.stringify({ 
                                    wordChoices: [wcItem],
                                    parentConfigJson
                                }),
                                answerJson: null,
                                score: null
                            }]
                        };
                        expandedActivities.push(extendedActivity);
                        console.log(`  → ${extensionType} 활동 추가됨 (문항 ${wcIdx + 1}/${activity.items.length})`);
                    });
                    return;
                }
                // 기타 확장: 모든 문항을 하나로 묶어 기존 로직 유지
                const extendedActivity: Activity = {
                    ...activity,
                    id: activity.id + Math.random(), // 임시 고유 ID
                    activityType: extensionType,
                    title: extensionType === 'PART_WRITING' ? '부분 영작' : extensionType,
                    items: [{
                        id: activity.id,
                        itemType: extensionType,
                        orderIndex: 0,
                        refType: 'WORD_CHOICE',
                        refId: activity.id,
                        contentJson: JSON.stringify({ 
                            wordChoices: activity.items,
                            parentConfigJson: activity.configJson // activity의 configJson도 전달 (titleTranslation 포함)
                        }),
                        answerJson: null,
                        score: null
                    }]
                };
                
                expandedActivities.push(extendedActivity);
                console.log(`  → ${extensionType} 활동 추가됨 (단어고르기 ${activity.items.length}개를 하나로 묶음)`);
            });
        }
    });
    
    // 2-1. WORD_CHOICE 확장 활동 추가 (allowedDbActivities에 없는 경우에도 확장 활동은 추가)
    // STARTER_STRUCTURE의 경우 WORD_CHOICE는 화면에 표시 안 하지만, 부분영작은 생성해야 함
    if (config.wordChoiceExtensions && config.wordChoiceExtensions.length > 0) {
        // filteredActivities에 없는 WORD_CHOICE만 처리 (중복 방지)
        const filteredWordChoiceIds = new Set(
            filteredActivities
                .filter(act => act.activityType === 'WORD_CHOICE')
                .map(act => act.id)
        );
        
        const notFilteredWordChoiceActivities = activities.filter(
            act => act.activityType === 'WORD_CHOICE' && !filteredWordChoiceIds.has(act.id)
        );
        
        notFilteredWordChoiceActivities.forEach(activity => {
            console.log(`[확장 전용] 단어고르기 데이터로 확장: ${config.wordChoiceExtensions!.join(', ')}`);
            console.log(`[확장 전용] 단어고르기 아이템 개수: ${activity.items.length}`);
            
            config.wordChoiceExtensions!.forEach(extensionType => {
                if (extensionType === 'PART_WRITING' && activity.items?.length) {
                    activity.items.forEach((wcItem, wcIdx) => {
                        let perItemTitleTranslation = '';
                        try {
                            const wcContent = wcItem?.contentJson ? (typeof wcItem.contentJson === 'string' ? JSON.parse(wcItem.contentJson) : wcItem.contentJson) : {};
                            perItemTitleTranslation = wcContent?.titleTranslation || '';
                        } catch (e) {
                            console.error('wcItem contentJson parse error:', e);
                        }
                        let parentConfigJson = activity.configJson;
                        try {
                            const parsedCfg = activity.configJson ? (typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson) : {};
                            parentConfigJson = JSON.stringify({ ...parsedCfg, titleTranslation: perItemTitleTranslation || parsedCfg.titleTranslation });
                        } catch (e) {
                            console.error('WORD_CHOICE parent config parse error:', e);
                        }
                        const extendedActivity: Activity = {
                            ...activity,
                            id: activity.id + Math.random(), // 임시 고유 ID
                            activityType: extensionType,
                            title: '부분 영작',
                            items: [{
                                id: wcItem.id,
                                itemType: extensionType,
                                orderIndex: wcIdx,
                                refType: 'WORD_CHOICE',
                                refId: wcItem.id,
                                contentJson: JSON.stringify({ 
                                    wordChoices: [wcItem],
                                    parentConfigJson
                                }),
                                answerJson: null,
                                score: null
                            }]
                        };
                        
                        expandedActivities.push(extendedActivity);
                        console.log(`  → [확장 전용] ${extensionType} 활동 추가됨 (문항 ${wcIdx + 1}/${activity.items.length})`);
                    });
                    return;
                }
                const extendedActivity: Activity = {
                    ...activity,
                    id: activity.id + Math.random(), // 임시 고유 ID
                    activityType: extensionType,
                    title: extensionType === 'PART_WRITING' ? '부분 영작' : extensionType,
                    items: [{
                        id: activity.id,
                        itemType: extensionType,
                        orderIndex: 0,
                        refType: 'WORD_CHOICE',
                        refId: activity.id,
                        contentJson: JSON.stringify({ 
                            wordChoices: activity.items,
                            parentConfigJson: activity.configJson
                        }),
                        answerJson: null,
                        score: null
                    }]
                };
                
                expandedActivities.push(extendedActivity);
                console.log(`  → [확장 전용] ${extensionType} 활동 추가됨 (단어고르기 ${activity.items.length}개를 하나로 묶음)`);
            });
        });
    }
    
    // 2-2. FLASH_CARD 확장 활동 추가 (allowedDbActivities에 포함되어 있어도 확장 생성은 항상 수행)
    // 주의: ESSAY, TRANSLATION은 이미 직접 변환했으므로 제외
    if (config.flashCardExtensions && config.flashCardExtensions.length > 0 && unitType !== 'ESSAY' && unitType !== 'TRANSLATION') {
        // 모든 FLASH_CARD를 기반으로 확장 생성 (확장 타입은 별도 activity이므로 중복 아님)
        const allFlashCardActivities = activities.filter(act => act.activityType === 'FLASH_CARD');
        
        allFlashCardActivities.forEach(activity => {
            console.log(`[확장 전용] 플래시카드 데이터로 확장 활동 생성: ${config.flashCardExtensions!.join(', ')}`);
            console.log(`[확장 전용] 플래시카드 아이템 개수: ${activity.items.length}`);
            
            config.flashCardExtensions!.forEach(extensionType => {
                // DRAG_MATCH: 모든 아이템을 하나로 묶음
                // SPELL_CHECK, ALPHABET_CHOICE: 각 아이템마다 개별 생성
                const shouldCreateIndividually = extensionType === 'SPELL_CHECK' || extensionType === 'ALPHABET_CHOICE';
                
                if (shouldCreateIndividually) {
                    // 각 플래시카드 아이템마다 개별 활동 생성
                    activity.items.forEach((flashCardItem, idx) => {
                        const extendedActivity: Activity = {
                            ...activity,
                            id: activity.id + Math.random(), // 임시 고유 ID
                            activityType: extensionType,
                            title: extensionType === 'SPELL_CHECK' ? '스펠 체크' :
                                   extensionType === 'ALPHABET_CHOICE' ? '알파벳 고르기' :
                                   extensionType,
                            // 현재 플래시카드 아이템의 데이터만 포함
                            items: [{
                                id: flashCardItem.id,
                                itemType: extensionType,
                                orderIndex: idx,
                                refType: 'FLASH_CARD',
                                refId: flashCardItem.id,
                                contentJson: flashCardItem.contentJson, // 개별 플래시카드 데이터
                                answerJson: null,
                                score: null
                            }]
                        };
                        
                        expandedActivities.push(extendedActivity);
                        console.log(`  → [확장 전용] ${extensionType} 활동 추가됨 (플래시카드 아이템 ${idx + 1}/${activity.items.length})`);
                    });
                } else {
                    // DRAG_MATCH: 모든 플래시카드 아이템을 하나로 묶음
                    const extendedActivity: Activity = {
                        ...activity,
                        id: activity.id + Math.random(), // 임시 고유 ID
                        activityType: extensionType,
                        title: extensionType === 'DRAG_MATCH' ? '드래그 매치' : extensionType,
                        // 모든 플래시카드 아이템을 하나의 아이템으로 묶음
                        items: [{
                            id: activity.id,
                            itemType: extensionType,
                            orderIndex: 0,
                            refType: 'FLASH_CARD',
                            refId: activity.id,
                            contentJson: JSON.stringify({ flashCards: activity.items }), // 모든 플래시카드 데이터 포함
                            answerJson: null,
                            score: null
                        }]
                    };
                    
                    expandedActivities.push(extendedActivity);
                    console.log(`  → [확장 전용] ${extensionType} 활동 추가됨 (플래시카드 ${activity.items.length}개를 하나로 묶음)`);
                }
            });
        });
    }
    
    console.log(`총 활동 개수: ${filteredActivities.length} → ${expandedActivities.length}`);
    
    // 2-3. ESSAY/TRANSLATION: PART_WRITING 데이터로 ENG_WRITING 생성
    if ((unitType === 'ESSAY' || unitType === 'TRANSLATION') && config.allowedDbActivities.includes('ENG_WRITING')) {
        const partWritingActivities = expandedActivities.filter(act => act.activityType === 'PART_WRITING');
        
        partWritingActivities.forEach(activity => {
            console.log('PART_WRITING 데이터로 ENG_WRITING 생성');
            
            // PART_WRITING의 configJson 데이터를 사용
            const engWritingActivity: Activity = {
                ...activity,
                id: activity.id + 1000, // 임시 고유 ID
                activityType: 'ENG_WRITING',
                title: '영작',
                items: [] // ENG_WRITING은 configJson에서 데이터 사용
            };
            
            expandedActivities.push(engWritingActivity);
            console.log('  → ENG_WRITING 활동 추가됨');
        });
    }
    
    console.log(`확장 후 총 활동 개수: ${expandedActivities.length}`);
    
    // 3. 유닛 타입별 활동 순서 정렬
    if (unitType === 'ESSAY' || unitType === 'TRANSLATION') {
        // 에세이/번역: 명시적 순서 지정
        // DICTATION 다음에 DRAG_MATCH, 그 다음 PART_WRITING, ENG_WRITING, FREE_WRITING
        const activityOrder = ['VIDEO_QUIZ', 'DICTATION', 'DRAG_MATCH', 'PART_WRITING', 'FILL_BLANK', 'ENG_WRITING', 'FREE_WRITING'];
        
        expandedActivities.sort((a, b) => {
            const aIndex = activityOrder.indexOf(a.activityType);
            const bIndex = activityOrder.indexOf(b.activityType);
            
            // 순서에 없는 타입은 뒤로
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            
            if (aIndex !== bIndex) return aIndex - bIndex;
            return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        });
        
        console.log('에세이/번역 유닛: 활동 순서 정렬 완료');
    } else if (unitType === 'STARTER_NUANCE') {
        // 스타터 뉘앙스: 명시적 순서 지정
        // 동영상 퀴즈 → 플래시카드 → 드래그매치 → 단어고르기 → 영작
        const activityOrder = ['VIDEO_QUIZ', 'FLASH_CARD', 'DRAG_MATCH', 'WORD_CHOICE', 'PART_WRITING'];
        
        expandedActivities.sort((a, b) => {
            const aIndex = activityOrder.indexOf(a.activityType);
            const bIndex = activityOrder.indexOf(b.activityType);
            
            // 순서에 없는 타입은 뒤로
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            
            if (aIndex !== bIndex) return aIndex - bIndex;
            return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        });
        
        console.log('스타터 뉘앙스 유닛: 활동 순서 정렬 완료');
    } else if (unitType === 'STARTER_STRUCTURE') {
        // 스타터 스트럭처: 명시적 순서 지정
        // 동영상 퀴즈 → 플래시카드 → 드래그매치 → 다이어그램 → 부분 영작
        const activityOrder = ['VIDEO_QUIZ', 'FLASH_CARD', 'DRAG_MATCH', 'DIAGRAM', 'PART_WRITING'];
        
        expandedActivities.sort((a, b) => {
            const aIndex = activityOrder.indexOf(a.activityType);
            const bIndex = activityOrder.indexOf(b.activityType);
            
            // 순서에 없는 타입은 뒤로
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            
            if (aIndex !== bIndex) return aIndex - bIndex;
            return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        });
        
        console.log('스타터 스트럭처 유닛: 활동 순서 정렬 완료');
    } else if (unitType === 'READING_BOOK') {
        // 리딩북: 명시적 순서 지정
        // 플래시카드 → 드래그매치 → 스토리&다이얼로그 → 스토리&다이얼로그_퀘스천
        const activityOrder = ['FLASH_CARD', 'DRAG_MATCH', 'STORY_DIALOG', 'STORY_DIALOG_QA'];
        
        expandedActivities.sort((a, b) => {
            const aIndex = activityOrder.indexOf(a.activityType);
            const bIndex = activityOrder.indexOf(b.activityType);
            
            // 순서에 없는 타입은 뒤로
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            
            if (aIndex !== bIndex) return aIndex - bIndex;
            return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        });
        
        console.log('리딩북 유닛: 활동 순서 정렬 완료');
    } else {
        // 다른 유닛: VIDEO_QUIZ만 맨 앞으로
        const videoQuizIndex = expandedActivities.findIndex(act => act.activityType === 'VIDEO_QUIZ');
        if (videoQuizIndex > 0) {
            const videoQuiz = expandedActivities.splice(videoQuizIndex, 1)[0];
            expandedActivities.unshift(videoQuiz);
            console.log('동영상 퀴즈를 맨 앞으로 이동했습니다.');
        }
    }
    
    console.log('최종 활동 순서:');
    expandedActivities.forEach((act, idx) => {
        console.log(`  ${idx + 1}. ${act.activityType} - ${act.title}`);
    });
    
    // 아이템을 펼치지 않고 전체를 하나로 처리하는 활동 타입들
    const SINGLE_ITEM_ACTIVITIES = [
        'DRAG_MATCH',
        'SPELL_CHECK', 
        'ALPHABET_CHOICE',
        'PART_WRITING',
        'STORY_DIALOG',
        'STORY_DIALOG_QA',
        'VIDEO_QUIZ',
        'DIAGRAM', // items가 없고 configJson에 데이터가 있음
        'DICTATION', // ESSAY는 WORD_CHOICE 데이터를 사용하는 단일 활동
        'FILL_BLANK', // items가 비어있어도 configJson에 데이터가 있음
        'ENG_WRITING', // items가 비어있어도 configJson에 데이터가 있음
        'FREE_WRITING' // items가 비어있어도 configJson에 데이터가 있음
    ];
    
    // 4. 모든 아이템을 flat하게 펼치기
    const flatItems: FlatItem[] = [];
    expandedActivities.forEach((activity, activityIndex) => {
        if (SINGLE_ITEM_ACTIVITIES.includes(activity.activityType)) {
            // 전체 활동을 하나의 아이템으로 처리
            flatItems.push({
                activity,
                item: activity.items[0] || {}, // 첫 번째 아이템 (또는 빈 객체)
                globalIndex: flatItems.length,
                activityIndex,
                itemIndex: 0
            });
            console.log(`${activity.activityType}: 전체 아이템을 하나로 처리 (${activity.items.length}개 포함)`);
        } else {
            // 아이템을 개별적으로 펼치는 활동들 (FLASH_CARD, WORD_CHOICE 등)
            activity.items.forEach((item, itemIndex) => {
                flatItems.push({
                    activity,
                    item,
                    globalIndex: flatItems.length,
                    activityIndex,
                    itemIndex
                });
            });
        }
    });
    
    console.log('=== flatItems 생성 완료 ===');
    console.log('flatItems 개수:', flatItems.length);
    console.log('flatItems 구성:', flatItems.map((item, idx) => `${idx}: ${item.activity.activityType}`));
    
    const totalItems = flatItems.length;
    const currentFlatItem = flatItems[step];
    const previousFlatItem = step > 0 ? flatItems[step - 1] : null;
    const isComplete = step >= totalItems;
    const currentResponse = responses[step];
    const CurrentActivityComponent = currentFlatItem ? ActivityComponents[currentFlatItem.activity.activityType] : null;
    
    // 정답 체크가 필요 없는 활동 타입들 (자유 답변 활동 등)
    // 모든 활동은 완료 조건이 있으므로 빈 배열
    const NO_ANSWER_CHECK_ACTIVITIES: string[] = [];
    // 현재 활동이 정답 체크가 필요 없는 타입인지
    const isNoAnswerCheckActivity = currentFlatItem && NO_ANSWER_CHECK_ACTIVITIES.includes(currentFlatItem.activity.activityType);
    const requiresSubmitValidation = !isHomework && Boolean(submitValidator);
    const isFlashCardActivity = currentFlatItem?.activity.activityType === 'FLASH_CARD';
    const previousIsFlashCardActivity = previousFlatItem?.activity.activityType === 'FLASH_CARD';
    const isSpellCheckActivity = currentFlatItem?.activity.activityType === 'SPELL_CHECK';
    const previousIsSpellCheckActivity = previousFlatItem?.activity.activityType === 'SPELL_CHECK';
    const isDictationActivity = currentFlatItem?.activity.activityType === 'DICTATION';
    const previousIsDictationActivity = previousFlatItem?.activity.activityType === 'DICTATION';
    const hidePrevButtonForFlashCard = !isComplete && isFlashCardActivity && !previousIsFlashCardActivity;
    const hidePrevButtonForSpellCheck = !isComplete && isSpellCheckActivity && !previousIsSpellCheckActivity;
    const hidePrevButtonForDictation = !isComplete && isDictationActivity && !previousIsDictationActivity;
    const hidePrevButton = hidePrevButtonForFlashCard || hidePrevButtonForSpellCheck || hidePrevButtonForDictation;
    const shouldRequireCorrectAnswer = !isNoAnswerCheckActivity && !isHomework;
    const needsCorrectAnswer = !isReadOnlyMode && shouldRequireCorrectAnswer && !requiresSubmitValidation && !isComplete && currentResponse?.isCorrect !== true;
    const needsHomeworkCompletion = !isReadOnlyMode && isHomework && !isComplete && (currentResponse?.isHomeworkReady !== true);
    const limitAlertMessage = isHomework
        ? '오늘 풀 수 있는 유닛을 모두 완료했어요.\n다음 숙제는 내일 진행할 수 있습니다.'
        : '오늘의 학습 제한량을 초과했습니다.\n다음 유닛은 내일 학습할 수 있습니다.';
    
    const registerSubmitValidator = (validator: (() => Promise<boolean> | boolean) | null) => {
        setSubmitValidator(() => validator);
    };

    const runSubmitValidation = async () => {
        if (isReadOnlyMode || isComplete || !requiresSubmitValidation) return true;
        if (submitValidator) {
            const result = await Promise.resolve(submitValidator());
            return result !== false;
        }
        return currentResponse?.isCorrect === true;
    };

    return (
        <main className='flex flex-1 w-full mt-10 bg-transparent shadow-md !rounded-t-2xl md:my-10 md:justify-center card '>
            <div className='flex flex-col flex-1 w-full'>
                <div className="flex justify-between items-center px-5 pt-5 pb-3 border-b border-gray-200">
                    <h1 className='text-lg font-bold'>{unitData.unitCategory || unitData.textbookTitle || '교재명'}</h1>
                    <span className='text-lg text-gray-500'>{unitData.unitTitle || '유닛명'}</span>
                </div>
                
                {/* <div className='px-5 pt-3'>
                    <div className='flex gap-2 items-center text-sm text-gray-600'>
                        <span>진행 상황:</span>
                        <span className='font-bold text-indigo-600'>
                            {isComplete ? totalItems : step + 1} / {totalItems}
                        </span>
                        {currentFlatItem && (
                            <span className='ml-2 text-white'>
                                ({currentFlatItem.activity.activityType} */}
                                {/* 전체를 하나로 처리하는 활동은 아이템 개수 표시 안 함 */}
                                {/* {!SINGLE_ITEM_ACTIVITIES.includes(currentFlatItem.activity.activityType) &&
                                    ` - ${currentFlatItem.itemIndex + 1}/${currentFlatItem.activity.items.length}`
                                })
                            </span>
                        )}
                    </div>
                </div> */}

                <section className='flex flex-col flex-1'>
                    <div className='flex-1'>
                        {isComplete ? (
                            <Complete />
                        ) : CurrentActivityComponent && currentFlatItem ? (
                            <CurrentActivityComponent 
                                key={`item-${step}`} // 각 아이템마다 고유한 key로 완전히 새로 마운트
                                activity={{
                                    ...currentFlatItem.activity,
                                    // 전체 아이템을 하나로 처리하는 활동은 모든 아이템 전달, 아니면 현재 아이템만
                                    items: SINGLE_ITEM_ACTIVITIES.includes(currentFlatItem.activity.activityType)
                                        ? currentFlatItem.activity.items // 모든 아이템 전달
                                        : [currentFlatItem.item] // 현재 아이템만 전달
                                }}
                                unitData={unitData}
                                onResponse={(response: any) => handleResponse(step, response)}
                                existingResponse={responses[step]} // 이전에 저장한 응답 전달 (이전 버튼으로 돌아왔을 때)
                                isHomework={isHomework} // 숙제 여부 전달 (정답 표시 제어용)
                                registerSubmitValidator={registerSubmitValidator}
                            />
                        ) : (
                            <div className='flex justify-center items-center h-64'>
                                <p className='text-gray-500'>
                                    해당 활동 타입({currentFlatItem?.activity.activityType})의 컴포넌트를 찾을 수 없습니다.
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <div className='flex gap-4 p-5 pb-3 w-full'>
                        <Button
                            intent="secondary"
                            className="w-full"
                            onClick={() => {
                                // 완료 화면에서 이전 버튼을 누르면 마지막 활동으로 이동
                                const prevStep = isComplete ? totalItems - 1 : Math.max(step - 1, 0);
                                console.log('=== 이전 버튼 클릭 ===');
                                console.log('현재 step:', step, '→ 이전 step:', prevStep);
                                console.log('isComplete:', isComplete);
                                console.log('이전 step의 응답:', responses[prevStep]);
                                setStep(prevStep);
                            }}
                            disabled={step === 0 && !isComplete || hidePrevButton}
                        >
                            이전
                        </Button>
                        <Button
                            className="w-full"
                            onClick={async () => {
                                console.log('=== 다음/완료 버튼 클릭 ===');
                                console.log('현재 step:', step);
                                console.log('총 아이템 수:', totalItems);
                                console.log('isComplete:', isComplete);

                                if (isReadOnlyMode) {
                                    if (isComplete) {
                                        goToDetailPage();
                                        return;
                                    }
                                    if (step >= totalItems) {
                                        return;
                                    }
                                    setStep(Math.min(step + 1, totalItems));
                                    return;
                                }
                                
                                // 완료 화면에서 "완료" 버튼 클릭 시 다음 유닛 이동 또는 리스트로 복귀
                                if (isComplete) {
                                    console.log('✅ 완료 버튼 클릭 - COMPLETED 상태 최종 저장 후 다음 동작 수행');
                                    setIsSaving(true);
                                    try {
                                        const responseJson = JSON.stringify(responses);
                                        await studentUnitApi.saveProgress(Number(unitId), {
                                            scheduleId: Number(scheduleId),
                                            responseJson,
                        progressPercentage: 100,
                        status: 'COMPLETED',
                        isHomework: isHomework
                                        });
                                        console.log('✅ 최종 완료 상태 저장 완료');

                                        const hasNextUnit = Boolean(unitData.nextUnitId);
                                        const remainingToday = unitData.dailyLimit?.remainingToday ?? 0;
                                        const isUnlimited = Boolean(unitData.dailyLimit?.unlimited);
                                        const limitDefined = Boolean(unitData.dailyLimit);
                                        const canProceed = !limitDefined || isUnlimited || remainingToday > 0;

                                        if (hasNextUnit && canProceed) {
                                            const searchParams = new URLSearchParams(window.location.search);
                                            searchParams.set('unitId', String(unitData.nextUnitId));
                                            navigate(`${window.location.pathname}?${searchParams.toString()}`);
                                            return;
                                        }

                                        if (hasNextUnit && !canProceed) {
                                            await Modal.alert(limitAlertMessage);
                                        }
                                    } catch (error) {
                                        console.error('완료 상태 저장 실패:', error);
                                        await Modal.alert('완료 정보를 저장하는 중 오류가 발생했습니다.');
                                    } finally {
                                        setIsSaving(false);
                                    }
                                    
                                    goToDetailPage();
                                    return;
                                }
                                
                                console.log('현재 활동:', flatItems[step]?.activity.activityType);
                                console.log('현재 응답:', responses[step]);
                                console.log('전체 응답:', responses);
                                
                                // step이 totalItems를 초과하면 더 이상 진행하지 않음
                                if (step >= totalItems) {
                                    console.log('⚠️ step이 totalItems를 초과했습니다. 진행을 중단합니다.');
                                    return;
                                }

                                const canProceed = await runSubmitValidation();
                                if (!canProceed) {
                                    console.log('제출 검증 실패로 진행을 중단합니다.');
                                    return;
                                }

                                setIsSaving(true);
                                try {
                                    // 현재 응답이 없으면 빈 응답 생성 (사용자가 아무것도 입력하지 않은 경우)
                                    let updatedResponses = { ...responses };
                                    const currentResponse = responses[step];
                                    
                                    // 응답이 없으면 빈 객체라도 저장 (진행 상황 추적용)
                                    if (!currentResponse) {
                                        console.log('⚠️ 현재 step의 응답이 없습니다. 빈 응답을 생성합니다.');
                                        updatedResponses[step] = { 
                                            activityType: flatItems[step]?.activity.activityType,
                                            isEmpty: true,
                                            timestamp: Date.now()
                                        };
                                    }
                                    
                                    // 녹음 파일이 있으면 S3에 업로드
                                    if (currentResponse?.recordingBlob) {
                                        console.log('녹음 파일 업로드 중...', currentResponse.recordingBlob);
                                        const uploadResult = await studentUnitApi.uploadRecording(currentResponse.recordingBlob);
                                        console.log('업로드 완료:', uploadResult.data.audioUrl);
                                        
                                        // Blob 대신 S3 URL로 교체
                                        updatedResponses[step] = {
                                            ...currentResponse,
                                            recordingUrl: uploadResult.data.audioUrl,
                                            recordingBlob: undefined // Blob 제거
                                        };
                                        
                                        // State도 업데이트
                                        setResponses(updatedResponses);
                                    }
                                    
                                    // STORY_DIALOG: 문장별 녹음 업로드 및 URL 치환
                                    const currentActivityType = flatItems[step]?.activity.activityType;
                                    if (currentActivityType === 'STORY_DIALOG' && currentResponse?.sentenceRecordings) {
                                        const newSentenceRecordings: Record<string, any> = { ...currentResponse.sentenceRecordings };
                                        for (const [sid, rec] of Object.entries(newSentenceRecordings)) {
                                            try {
                                                // Blob이 있거나 blob: URL이면 업로드
                                                const needsUpload = (rec as any)?.blob instanceof Blob || (typeof (rec as any)?.url === 'string' && (rec as any).url.startsWith('blob:'));
                                                if (needsUpload) {
                                                    console.log(`문장 ${sid} 녹음 업로드 중...`, (rec as any)?.blob || (rec as any)?.url);
                                                    const uploadRes = await studentUnitApi.uploadRecording((rec as any)?.blob as Blob);
                                                    newSentenceRecordings[sid] = {
                                                        ...(rec as any),
                                                        url: uploadRes.data.audioUrl,
                                                        blob: undefined,
                                                        recorded: true,
                                                    };
                                                }
                                            } catch (e) {
                                                console.error(`문장 ${sid} 업로드 실패:`, e);
                                            }
                                        }
                                        updatedResponses[step] = {
                                            ...currentResponse,
                                            sentenceRecordings: newSentenceRecordings,
                                        };
                                        setResponses(updatedResponses);
                                    }

                                    // STORY_DIALOG_QA: 질문별 녹음 업로드 및 URL 치환
                                    if (currentActivityType === 'STORY_DIALOG_QA' && currentResponse?.questionAnswers) {
                                        const newQuestionAnswers: Record<string, any> = { ...currentResponse.questionAnswers };
                                        for (const [qid, ans] of Object.entries(newQuestionAnswers)) {
                                            try {
                                                const needsUpload = (ans as any)?.blob instanceof Blob || (typeof (ans as any)?.url === 'string' && (ans as any).url.startsWith('blob:'));
                                                if (needsUpload) {
                                                    console.log(`질문 ${qid} 녹음 업로드 중...`, (ans as any)?.blob || (ans as any)?.url);
                                                    const uploadRes = await studentUnitApi.uploadRecording((ans as any)?.blob as Blob);
                                                    newQuestionAnswers[qid] = {
                                                        ...(ans as any),
                                                        url: uploadRes.data.audioUrl,
                                                        blob: undefined,
                                                        recorded: true,
                                                    };
                                                }
                                            } catch (e) {
                                                console.error(`질문 ${qid} 업로드 실패:`, e);
                                            }
                                        }
                                        updatedResponses[step] = {
                                            ...currentResponse,
                                            questionAnswers: newQuestionAnswers,
                                        };
                                        setResponses(updatedResponses);
                                    }
                                    
                                    console.log('=== 저장할 응답 데이터 ===');
                                    console.log('Step:', step);
                                    console.log('Updated Responses:', updatedResponses);
                                    console.log('학습/숙제 구분:', isHomework ? '숙제' : '학습');
                                    
                                    // 응답 저장 (업데이트된 responses 사용)
                                const responseJson = JSON.stringify(updatedResponses);
                                // progressPercentage 계산 (완료 보호: 재진입 시 완료 강등 방지)
                                const currentProgress = step + 1;
                                const computedProgress = Math.min(100, Math.round((currentProgress / totalItems) * 100));
                                const persistProgress = (isHomework && wasCompleted) ? 100 : computedProgress;
                                const persistStatus = (isHomework && wasCompleted)
                                    ? 'COMPLETED'
                                    : (currentProgress >= totalItems ? 'COMPLETED' : 'IN_PROGRESS');

                                await studentUnitApi.saveProgress(Number(unitId), {
                                    scheduleId: Number(scheduleId),
                                    responseJson,
                                    progressPercentage: persistProgress,
                                    status: persistStatus,
                                    isHomework: isHomework
                                });

                                    console.log('응답 저장 완료 - Progress:', persistProgress + '%');
                                    
                                    // 다음 유닛으로 자동 이동 로직
                                    const nextStep = step + 1;
                                    const isLastStep = nextStep >= totalItems;
                                    
                                    if (isLastStep && unitData.nextUnitId) {
                                        const remainingToday = unitData.dailyLimit?.remainingToday ?? 0;
                                        const isUnlimited = Boolean(unitData.dailyLimit?.unlimited);
                                        const limitDefined = Boolean(unitData.dailyLimit);
                                        const canProceed = !limitDefined || isUnlimited || remainingToday > 1;

                                        if (canProceed) {
                                            console.log('다음 유닛으로 자동 이동:', unitData.nextUnitId);
                                            const searchParams = new URLSearchParams(window.location.search);
                                            searchParams.set('unitId', String(unitData.nextUnitId));
                                            navigate(`${window.location.pathname}?${searchParams.toString()}`);
                                            return;
                                        } else {
                                            // 제한 초과 알림
                                            await Modal.alert(limitAlertMessage);
                                            goToDetailPage();
                                            return;
                                        }
                                    }
                                    
                                    // 다음 step으로 이동 (완료 화면 포함)
                                    setStep(nextStep);
                                } catch (error) {
                                    console.error('저장 실패:', error);
                                    alert('저장에 실패했습니다.');
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                            disabled={isSaving || needsCorrectAnswer || needsHomeworkCompletion}
                        >
                            {isSaving ? '저장 중...' : (isComplete ? '완료' : '다음')}
                        </Button>
                    </div>
                </section>
            </div>
        </main>
    )   
}